import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Package } from 'lucide-react';
import { useAppAccess, APP_LABELS } from '../../context/AppAccessContext';
import { useAuth } from '../../context/AuthContext';
import { useModule } from '../../context/ModuleContext';
import { APPLICATION_NAME } from '../../constants/AppConfig';
import AppLogo from '../../images/logo.png';
import './AppHomePage.css';

const APP_LANDING_ROUTE: Record<string, string> = {
  platform: '/settings/company',
  inventory: '/dashboard',
  sales: '/sales/dashboard',
  restaurant: '/restaurant/orders',
  carRental: '/car-rental',
  automobile: '/automobile/vehicles',
};

const AdminIcon = () => (
  <svg viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
    <defs>
      <linearGradient id="ah-plat-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1e3a8a" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
      <radialGradient id="ah-plat-sh" cx="35%" cy="28%" r="60%">
        <stop offset="0%" stopColor="white" stopOpacity="0.22" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="36" cy="36" r="34" fill="url(#ah-plat-bg)" />
    <circle cx="36" cy="36" r="34" fill="url(#ah-plat-sh)" />
    {/* Shield */}
    <path d="M36 13 L51 21 L51 37 Q51 51 36 59 Q21 51 21 37 L21 21 Z" fill="white" opacity="0.93" />
    {/* Gear teeth – 6 at 60° intervals */}
    {[0, 60, 120, 180, 240, 300].map(a => (
      <rect key={a} x="34.5" y="23" width="3" height="7" rx="1.5" fill="#f59e0b" transform={`rotate(${a} 36 36)`} />
    ))}
    <circle cx="36" cy="36" r="8" fill="#f59e0b" />
    <circle cx="36" cy="36" r="4.5" fill="white" />
    <circle cx="36" cy="36" r="2" fill="#1e3a8a" />
    {/* Corner sparkle dots */}
    <circle cx="62" cy="14" r="2.5" fill="#fbbf24" opacity="0.85" />
    <circle cx="10" cy="18" r="1.8" fill="#fbbf24" opacity="0.65" />
  </svg>
);

const InventoryIcon = () => (
  <svg viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
    <defs>
      <linearGradient id="ah-inv-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1e3a8a" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
      <radialGradient id="ah-inv-sh" cx="35%" cy="28%" r="60%">
        <stop offset="0%" stopColor="white" stopOpacity="0.22" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="36" cy="36" r="34" fill="url(#ah-inv-bg)" />
    <circle cx="36" cy="36" r="34" fill="url(#ah-inv-sh)" />
    {/* Isometric 3D box */}
    <polygon points="36,14 57,25 36,36 15,25" fill="white" opacity="0.95" />
    <polygon points="15,25 36,36 36,57 15,46" fill="white" opacity="0.68" />
    <polygon points="57,25 57,46 36,57 36,36" fill="white" opacity="0.82" />
    {/* Subtle edge lines */}
    <line x1="36" y1="14" x2="36" y2="36" stroke="#60a5fa" strokeWidth="1" opacity="0.5" />
    <line x1="36" y1="36" x2="36" y2="57" stroke="#3b82f6" strokeWidth="1" opacity="0.4" />
    {/* Tape strip across top face */}
    <polygon points="36,22 46,17 46,20 36,25" fill="#f59e0b" opacity="0.85" />
    <polygon points="36,22 26,17 26,20 36,25" fill="#f59e0b" opacity="0.85" />
    {/* Gold star sparkle top-right */}
    <path d="M59 10 L60.2 13.5 L64 13.5 L61 15.8 L62.1 19.5 L59 17.2 L55.9 19.5 L57 15.8 L54 13.5 L57.8 13.5 Z" fill="#fbbf24" />
  </svg>
);

const SalesIcon = () => (
  <svg viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
    <defs>
      <linearGradient id="ah-sales-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1e3a8a" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
      <radialGradient id="ah-sales-sh" cx="35%" cy="28%" r="60%">
        <stop offset="0%" stopColor="white" stopOpacity="0.22" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="36" cy="36" r="34" fill="url(#ah-sales-bg)" />
    <circle cx="36" cy="36" r="34" fill="url(#ah-sales-sh)" />
    {/* Shopping bag body */}
    <path d="M21 35 L51 35 L48 59 Q48 61 46 61 L26 61 Q24 61 24 59 Z" fill="white" opacity="0.93" />
    {/* Fold crease */}
    <line x1="21" y1="39" x2="51" y2="39" stroke="#93c5fd" strokeWidth="1.5" opacity="0.55" />
    {/* Handle arc */}
    <path d="M29 35 Q29 24 36 24 Q43 24 43 35" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" opacity="0.93" />
    {/* Gold star badge */}
    <circle cx="53" cy="19" r="9" fill="#f59e0b" />
    <path d="M53 13 L54.5 17.2 L59 17.2 L55.5 19.8 L56.8 24 L53 21.5 L49.2 24 L50.5 19.8 L47 17.2 L51.5 17.2 Z" fill="white" />
    {/* Tag line on bag */}
    <line x1="36" y1="44" x2="36" y2="54" stroke="#93c5fd" strokeWidth="1.2" strokeDasharray="2,2" opacity="0.5" />
  </svg>
);

const RestaurantIcon = () => (
  <svg viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
    <defs>
      <linearGradient id="ah-rest-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1e3a8a" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
      <radialGradient id="ah-rest-sh" cx="35%" cy="28%" r="60%">
        <stop offset="0%" stopColor="white" stopOpacity="0.22" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="36" cy="36" r="34" fill="url(#ah-rest-bg)" />
    <circle cx="36" cy="36" r="34" fill="url(#ah-rest-sh)" />
    {/* Plate rings */}
    <circle cx="36" cy="44" r="21" fill="none" stroke="white" strokeWidth="2" opacity="0.3" />
    <circle cx="36" cy="44" r="14" fill="white" opacity="0.13" />
    {/* Cloche dome */}
    <path d="M19 44 Q19 28 36 28 Q53 28 53 44 Z" fill="white" opacity="0.85" />
    <line x1="17" y1="44" x2="55" y2="44" stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity="0.95" />
    {/* Cloche handle knob */}
    <rect x="32" y="22" width="8" height="6" rx="3" fill="#f59e0b" />
    {/* Gold dome accent line */}
    <path d="M24 40 Q36 34 48 40" fill="none" stroke="#fbbf24" strokeWidth="1.2" opacity="0.6" />
    {/* Sparkle dots */}
    <circle cx="61" cy="14" r="2.5" fill="#fbbf24" opacity="0.9" />
    <circle cx="11" cy="20" r="1.8" fill="#fbbf24" opacity="0.7" />
  </svg>
);

const CarRentalIcon = () => (
  <svg viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
    <defs>
      <linearGradient id="ah-car-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1e3a8a" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
      <radialGradient id="ah-car-sh" cx="35%" cy="28%" r="60%">
        <stop offset="0%" stopColor="white" stopOpacity="0.22" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="36" cy="36" r="34" fill="url(#ah-car-bg)" />
    <circle cx="36" cy="36" r="34" fill="url(#ah-car-sh)" />
    {/* Car chassis */}
    <path d="M8 42 L8 37 Q8 35 10 35 L21 35 Q25 29 29 27 L46 27 Q52 29 55 35 L63 35 Q65 35 65 37 L65 42 Q65 44 63 44 L10 44 Q8 44 8 42 Z" fill="white" opacity="0.93" />
    {/* Cabin windows */}
    <path d="M28 35 L31 28 L44 28 L48 35 Z" fill="#60a5fa" opacity="0.72" />
    {/* Window divider */}
    <line x1="38.5" y1="28" x2="38.5" y2="35" stroke="white" strokeWidth="1.5" opacity="0.85" />
    {/* Roof rack bar */}
    <line x1="31" y1="27.5" x2="44" y2="27.5" stroke="#fbbf24" strokeWidth="1.5" opacity="0.8" />
    {/* Front wheel */}
    <circle cx="19" cy="44" r="8" fill="#f59e0b" />
    <circle cx="19" cy="44" r="4" fill="#1e3a8a" />
    <circle cx="19" cy="44" r="1.5" fill="#f59e0b" />
    {/* Rear wheel */}
    <circle cx="52" cy="44" r="8" fill="#f59e0b" />
    <circle cx="52" cy="44" r="4" fill="#1e3a8a" />
    <circle cx="52" cy="44" r="1.5" fill="#f59e0b" />
    {/* Headlight */}
    <ellipse cx="64" cy="38.5" rx="2.5" ry="2" fill="#fbbf24" opacity="0.9" />
    {/* Ground line */}
    <line x1="6" y1="52" x2="66" y2="52" stroke="white" strokeWidth="1.5" opacity="0.18" />
    {/* Road dashes */}
    <line x1="24" y1="56" x2="30" y2="56" stroke="white" strokeWidth="1.5" opacity="0.25" />
    <line x1="34" y1="56" x2="40" y2="56" stroke="white" strokeWidth="1.5" opacity="0.25" />
    <line x1="44" y1="56" x2="50" y2="56" stroke="white" strokeWidth="1.5" opacity="0.25" />
  </svg>
);

const AutoMobileIcon = () => (
  <svg viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg" width="64" height="64">
    <defs>
      <linearGradient id="ah-auto-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#134e4a" />
        <stop offset="100%" stopColor="#0d9488" />
      </linearGradient>
      <radialGradient id="ah-auto-sh" cx="35%" cy="28%" r="60%">
        <stop offset="0%" stopColor="white" stopOpacity="0.22" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="36" cy="36" r="34" fill="url(#ah-auto-bg)" />
    <circle cx="36" cy="36" r="34" fill="url(#ah-auto-sh)" />
    {/* Wrench body */}
    <path d="M22 50 L40 32 Q37 27 40 22 Q45 17 51 20 L45 26 L46 30 L50 31 L56 25 Q59 31 54 36 Q49 41 44 38 L26 56 Q23 59 20 56 Q17 53 20 50 Z" fill="white" opacity="0.93" />
    {/* Gold bolt accent */}
    <circle cx="23" cy="53" r="3" fill="#f59e0b" />
    {/* Sparkle dots */}
    <circle cx="60" cy="14" r="2.5" fill="#fbbf24" opacity="0.85" />
    <circle cx="10" cy="20" r="1.8" fill="#fbbf24" opacity="0.65" />
  </svg>
);

const APP_ICONS: Record<string, React.ReactNode> = {
  platform:   <AdminIcon />,
  inventory:  <InventoryIcon />,
  sales:      <SalesIcon />,
  restaurant: <RestaurantIcon />,
  carRental:  <CarRentalIcon />,
  automobile: <AutoMobileIcon />,
};

const APP_DESCRIPTIONS: Record<string, string> = {
  platform:   'appHome.desc.platform',
  inventory:  'appHome.desc.inventory',
  sales:      'appHome.desc.sales',
  restaurant: 'appHome.desc.restaurant',
  carRental:  'appHome.desc.carRental',
  automobile: 'appHome.desc.automobile',
};

const APP_ACCENT: Record<string, string> = {
  platform:   'app-card--gray',
  inventory:  'app-card--blue',
  sales:      'app-card--emerald',
  restaurant: 'app-card--amber',
  carRental:  'app-card--car',
  automobile: 'app-card--teal',
};

const AppHomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { availableApps, switchApp } = useAppAccess();
  const { user, logout } = useAuth();
  const { company } = useModule();

  const handleSelect = (appCode: string) => {
    switchApp(appCode);
    navigate(APP_LANDING_ROUTE[appCode] ?? '/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-home-page">
      <header className="app-home-header">
        <div className="app-home-header-left">
          <img src={AppLogo} alt={APPLICATION_NAME} className="app-home-logo" />
          <div>
            <div className="app-home-app-name">{APPLICATION_NAME}</div>
            {company && <div className="app-home-company">{company.name}</div>}
          </div>
        </div>
        <div className="app-home-header-right">
          {user && (
            <span className="app-home-user">
              {user.firstName} {user.lastName}
            </span>
          )}
          <button className="app-home-logout" onClick={handleLogout} title={t('appHome.logout')}>
            <LogOut size={16} strokeWidth={2} />
            <span>{t('appHome.logout')}</span>
          </button>
        </div>
      </header>

      <main className="app-home-main">
        <div className="app-home-welcome">
          <h1 className="app-home-title">{t('appHome.title')}</h1>
          <p className="app-home-subtitle">{t('appHome.subtitle')}</p>
        </div>

        {availableApps.length === 0 ? (
          <div className="app-home-empty">
            <Package size={48} strokeWidth={1} />
            <p>{t('appHome.noApps')}</p>
          </div>
        ) : (
          <div className="app-home-grid">
            {availableApps.map((appCode) => (
              <button
                key={appCode}
                className={`app-card ${APP_ACCENT[appCode] ?? 'app-card--gray'}`}
                onClick={() => handleSelect(appCode)}
              >
                <div className="app-card-icon">
                  {APP_ICONS[appCode] ?? <InventoryIcon />}
                </div>
                <div className="app-card-label">{APP_LABELS[appCode] ?? appCode}</div>
                <div className="app-card-desc">
                  {t(APP_DESCRIPTIONS[appCode] ?? 'appHome.desc.default')}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AppHomePage;
